import { Dashboard, FolderDetails, ImportExport, WorkflowAutomation } from "@carbon/icons-react";
import { SideNav, SideNavItems, SideNavLink, SideNavMenu, SideNavMenuItem } from "@carbon/react";
import React from "react";
import { matchPath, useNavigate } from "react-router-dom";

import { AuthorizedMenu, AuthorizedItem } from "src/authentication/AuthorizedMenu.tsx";
import { Scope, ScopeResource } from "src/authentication/PermissionsProvider";
import { useT } from "src/internationalization";
import "./Sidebar.scss";

type SideBarProps = {
  showMenu: boolean;
  pathName: string;
};

const Sidebar: React.FC<SideBarProps> = ({ showMenu, pathName }) => {
  const t = useT();
  const navigate = useNavigate();

  return (
    <SideNav
      aria-label="Side navigation"
      isRail={showMenu ? undefined : true}
      isFixedNav={showMenu ? true : undefined}
      isPersistent
      expanded={showMenu || undefined}
    >
      <SideNavItems isSideNavExpanded>
        <AuthorizedMenu
          menuItemsPermissions={[
            { resource: ScopeResource.ALERT, scopes: [Scope.VIEW] },
            { resource: ScopeResource.CASE, scopes: [Scope.VIEW] },
            { resource: ScopeResource.CUSTOMER, scopes: [Scope.VIEW] },
          ]}
        >
          <SideNavMenu
            isActive={
              pathName.includes("alerts") ||
              pathName.includes("cases") ||
              pathName.includes("customers")
            }
            renderIcon={FolderDetails}
            title={t("sidebar.cases")}
            defaultExpanded={true}
            isSideNavExpanded={true}
          >
            <AuthorizedItem
              requiredPermissions={[{ resource: ScopeResource.ALERT, scopes: [Scope.VIEW] }]}
            >
              <SideNavMenuItem
                isActive={pathName.startsWith("/alert")}
                onClick={() => navigate("/alerts/notAssigned")}
                className="cursor-pointer"
              >
                {t("sidebar.alerts")}
              </SideNavMenuItem>
            </AuthorizedItem>
            <AuthorizedItem
              requiredPermissions={[{ resource: ScopeResource.CASE, scopes: [Scope.VIEW] }]}
            >
              <SideNavMenuItem
                isActive={pathName.startsWith("/case")}
                onClick={() => navigate("/cases")}
                className="cursor-pointer"
              >
                {t("sidebar.cases")}
              </SideNavMenuItem>
            </AuthorizedItem>
            <AuthorizedItem
              requiredPermissions={[{ resource: ScopeResource.CUSTOMER, scopes: [Scope.VIEW] }]}
            >
              <SideNavMenuItem
                isActive={pathName.startsWith("/customer")}
                onClick={() => navigate("/customers")}
                className="cursor-pointer"
              >
                {t("page.customers.title")}
              </SideNavMenuItem>
            </AuthorizedItem>
          </SideNavMenu>
        </AuthorizedMenu>
        <AuthorizedMenu
          menuItemsPermissions={[
            { resource: ScopeResource.RULE, scopes: [Scope.VIEW] },
            { resource: ScopeResource.SCENARIO, scopes: [Scope.VIEW] },
            { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.VIEW] },
          ]}
        >
          <SideNavMenu
            renderIcon={WorkflowAutomation}
            title={t("sidebar.scoring")}
            defaultExpanded={true}
            isActive={pathName.includes("scenarios") || pathName.includes("blacklists")}
            isSideNavExpanded={true}
          >
            <AuthorizedItem
              requiredPermissions={[{ resource: ScopeResource.RULE, scopes: [Scope.VIEW] }]}
            >
              <SideNavMenuItem
                isActive={!!matchPath("/rules", pathName) || !!matchPath("/rules/:code", pathName)}
                onClick={() => navigate("/rules")}
                className="cursor-pointer"
              >
                {t("rules.title")}
              </SideNavMenuItem>
            </AuthorizedItem>
            <AuthorizedItem
              requiredPermissions={[{ resource: ScopeResource.SCENARIO, scopes: [Scope.VIEW] }]}
            >
              <SideNavMenuItem
                isActive={pathName.startsWith("/scenarios")}
                onClick={() => navigate("/scenarios")}
                className="cursor-pointer"
              >
                {t("scenarios.title")}
              </SideNavMenuItem>
            </AuthorizedItem>
            <AuthorizedItem
              requiredPermissions={[
                { resource: ScopeResource.BLACKLIST_ELEMENT, scopes: [Scope.VIEW] },
              ]}
            >
              <SideNavMenuItem
                isActive={pathName.startsWith("/blacklists")}
                onClick={() => navigate("/blacklists")}
                className="cursor-pointer"
              >
                {t("sidebar.blacklists")}
              </SideNavMenuItem>
            </AuthorizedItem>
          </SideNavMenu>
        </AuthorizedMenu>

        <SideNavLink
          renderIcon={Dashboard}
          isActive={pathName.startsWith("/reporting")}
          onClick={() => navigate("/reporting")}
          className="cursor-pointer"
        >
          {t("sidebar.reporting")}
        </SideNavLink>
        <AuthorizedMenu
          menuItemsPermissions={[
            {
              resource: ScopeResource.IMPORT,
              scopes: [Scope.VIEW],
            },
          ]}
        >
          <SideNavMenu
            renderIcon={ImportExport}
            title={t("sidebar.import")}
            defaultExpanded={true}
            isActive={
              pathName.includes("specifications") ||
              pathName.includes("job") ||
              pathName.includes("import-export")
            }
            isSideNavExpanded={true}
          >
            <AuthorizedItem
              requiredPermissions={[
                {
                  resource: ScopeResource.IMPORT,
                  scopes: [Scope.VIEW],
                },
              ]}
            >
              <SideNavMenuItem
                isActive={pathName.startsWith("/specification")}
                onClick={() => navigate("/specifications")}
                className="cursor-pointer"
              >
                {t("sidebar.import.specification")}
              </SideNavMenuItem>

              <SideNavMenuItem
                isActive={pathName.startsWith("/job")}
                onClick={() => navigate("/jobs")}
                className="cursor-pointer"
              >
                {t("sidebar.jobs")}
              </SideNavMenuItem>

              <SideNavMenuItem
                isActive={pathName.endsWith("/import-export")}
                onClick={() => navigate("/import-export")}
                className="cursor-pointer"
              >
                {t("sidebar.files")}
              </SideNavMenuItem>
            </AuthorizedItem>
          </SideNavMenu>
        </AuthorizedMenu>
      </SideNavItems>
    </SideNav>
  );
};

export default Sidebar;
